import React, {useState, useEffect} from 'react';
import cx from 'classnames';
import {useRouter} from 'next/router';
import {
  Formik, Form, useFormikContext,
} from 'formik';
import moment from 'moment-timezone';
import * as yup from 'yup';
import _ from 'lodash';

import {CheckCircleIcon} from '@heroicons/react/solid';
import {BASE_URL} from '../../constants/config';
import {getTags, createResumeReviewRequest} from '../../api/api';
import {Feature} from '../../../../lib/constants/tags';

import {
  TextArea, TextInput, DropFileInput, FormikDropdownInput,
} from '../../components/Forms';
import Button, {ButtonSizes} from '../../components/ButtonNew';
import Card from '../../components/Card';

import {useAppContext, useAnalytics} from '../../hooks';
import {encodeFileAsync, roleToRoleCategoryOptions} from '../../util';

const Resume = () => {
  const {setFieldValue} = useFormikContext();
  return (
    <DropFileInput
      label="Resume (PDF only, max 7MB)"
      id="resume"
      name="resume"
      accept="application/pdf"
      onChange={(event) => setFieldValue('resume', event.currentTarget.files[0])}
      required
    />
  );
};

const pricingPlans = [
  {
    title: 'Review over email',
    type: 'email',
    description: 'Get your resume reviewed in detail by a FAANG recruiter and receive notes over email within 5 business days.',
    price: null,
  },
  {
    title: 'Live 45-min video call',
    type: 'call',
    description: 'Chat 1-1 with a FAANG recruiter to review your resume and receive feedback in real-time. Schedule at your convenience.',
    price: null,
  },
];

const initialValues = {
  role: '',
  jobURL: '',
  email: '',
  linkedin: '',
  resume: null,
  notes: '',
  type: 'email',
};

const LoggedOutSchema = yup.object({
  role: yup.string().required('Required'),
  jobURL: yup.string(),
  email: yup.string().email('Must be a valid email').max(255).required('Required'),
  linkedin: yup.string().matches(/linkedin\.com\//, 'Must be a valid LinkedIn URL').required('Required'),
  resume: yup.mixed().required('Required'),
  notes: yup.string().max(500, '500 characters limit. If you have additional notes to share or specific requests for your resume review, we recommend purchasing a live video call to discuss more details with your assigned reviewer.').required('Required'),
  type: yup.string().required('Required'),
});

const LoggedInSchema = yup.object({
  role: yup.string().required('Required'),
  jobURL: yup.string(),
  linkedin: yup.string().matches(/linkedin\.com\//, 'Must be a valid LinkedIn URL').required('Required'),
  resume: yup.mixed().required('Required'),
  notes: yup.string().max(500, '500 characters limit. If you have additional notes to share or specific requests for your resume review, we recommend purchasing a live video call to discuss more details with your assigned reviewer.').required('Required'),
  type: yup.string().required('Required'),
});

export default function ResumeForm() {
  const router = useRouter();
  const {tracker} = useAnalytics();
  const [error, setError] = useState(null);
  const [tags, setTags] = useState(null);
  const [submittedRequest, setSubmittedRequest] = useState(null);
  const {from, coach, name} = router.query;
  const {catalog, currentUser, onCheckout} = useAppContext();

  // pre-populate if user has gone through call planCard
  if (from) initialValues.type = 'call';

  // populate prices
  pricingPlans[0].price = catalog?.RESUME_REVIEW.priceString;
  pricingPlans[1].price = catalog?.RESUME_REVIEW_CALL.priceString;

  const roleCategoryOptions = roleToRoleCategoryOptions(tags?.role, tags?.['role-category']);

  useEffect(() => {
    getTags({feature: Feature.Questions}).then(setTags);
    tracker.resumeViewForm();
  }, []);

  const toAPIFormat = async ({
    role,
    jobURL,
    email,
    linkedin,
    resume,
    notes,
    type,
    timezone,
  }) => ({
    role,
    jobURL,
    email,
    linkedin,
    resume: await encodeFileAsync(resume),
    notes,
    type,
    timezone,
  });

  const handleSubmit = async (values, {setSubmitting}) => {
    setError(null);
    setSubmitting(true);
    try {
      const planCatalogId = values.type === 'email' ? catalog.RESUME_REVIEW.name : catalog.RESUME_REVIEW_CALL.name;

      // eslint-disable-next-line no-param-reassign
      values.timezone = moment.tz.guess(new Date()) || 'America/Los Angeles';

      const data = await toAPIFormat(values);
      if (currentUser) data.email = currentUser.email;
      if (coach) data.coachSlug = coach; // allow requesting of specific reviewer
      const request = await createResumeReviewRequest(data);
      setSubmittedRequest(request);
      const options = {
        email: values.email,
        resumeRequestId: request.id,
        cancelUrl: `${BASE_URL}/resume/upload?payment_canceled=true`,
      };

      const products = [{
        id: planCatalogId,
        quantity: 1,
      }];
      onCheckout(products, currentUser, options);
      tracker.resumeSubmit();
      setSubmitting(false);
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 pt-6 pb-20">
      <h1 className="w-full block my-2 md:my-4 text-xl md:text-4xl text-center font-bold text-gray-900">
        Upload resume
      </h1>

      <span className="block w-full md:w-3/5 mx-auto mb-1 text-sm md:text-base text-center font-medium leading-tight text-gray-600">
        Your perfect resume is just one step away. These questions will help us match you
        to an expert reviewer and ensure your resume is tailored to the right job.
      </span>

      {coach && (
        <span className="block w-full md:w-3/5 mx-auto mb-1 text-sm md:text-base text-center font-medium leading-tight text-gray-600 mt-2">
          Reviewer requested:
          {' '}
          <b>{name}</b>
        </span>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={currentUser ? LoggedInSchema : LoggedOutSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({values, setFieldValue, isSubmitting}) => (
          <Form className="w-full flex flex-col items-center mt-10">
            <div className="w-full lg:w-3/5">
              <Card className="mt-5 mb-5">
                {submittedRequest ? (
                  <span className="block w-full mx-auto mb-1 text-sm md:text-base text-center font-medium leading-tight text-gray-600">
                    Directing you to payment. Hold on!
                  </span>
                ) : (
                  <fieldset disabled={isSubmitting}>

                    <FormikDropdownInput
                      label="What role are you applying to?"
                      name="role"
                      options={roleCategoryOptions}
                      showFilter
                      showReset={false}
                      highlightActive={false}
                      className="w-full mr-2"
                      required
                    />

                    <TextInput
                      label="Job listing URL (if any)"
                      name="jobURL"
                      type="text"
                    />

                    {!currentUser && (
                      <TextInput
                        label="Email address"
                        name="email"
                        type="text"
                        required
                      />
                    )}

                    <TextInput
                      label="LinkedIn profile"
                      name="linkedin"
                      type="text"
                      placeholder="https://linkedin.com/..."
                      required
                    />

                    <Resume />

                    <TextArea
                      label="What else you would like the reviewer to know?"
                      name="notes"
                      type="text"
                      placeholder="E.g., are you switching careers? What are the most important ideas you want conveyed?"
                      required
                    />

                    <div className="font-medium text-base text-gray-800">What kind of review do you want?</div>
                    <div className="mt-1 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                      {pricingPlans.map((pricingPlan) => (
                        <button
                          type="button"
                          key={pricingPlan.title}
                          onClick={() => { setFieldValue('type', pricingPlan.type); }}
                          className={cx(values.type === pricingPlan.type ? 'border-indigo-500 ring-2 ring-indigo-500' : 'border-gray-300',
                            'grid grid-cols-1 text-left rounded-lg border bg-white p-4 shadow-sm focus:outline-none')}
                        >
                          <div className="flex justify-between">
                            <div className="font-semibold">{pricingPlan.title}</div>
                            <CheckCircleIcon
                              className={cx(values.type === pricingPlan.type ? '' : 'invisible', 'h-5 w-5 text-indigo-600')}
                              aria-hidden="true"
                            />
                          </div>
                          <div className="pt-1">{pricingPlan.description}</div>
                          <div className="font-semibold pt-3 self-end">
                            {pricingPlan.price}
                          </div>
                        </button>
                      ))}
                    </div>

                  </fieldset>
                )}
                <div className="flex mt-4 w-full">
                  {!submittedRequest && (
                    <Button
                      type="submit"
                      className="w-full"
                      size={ButtonSizes.LARGE}
                      loading={isSubmitting}
                      disabled={submittedRequest}
                    >
                      Checkout
                    </Button>
                  )}
                </div>
                {error && (
                  <div className="mt-2 text-base font-medium leading-tight text-red-600">
                    {error.message}
                  </div>
                )}
              </Card>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
